/* Web Push notification helpers. */
import api from "@/lib/api";

const VAPID_PUBLIC_KEY = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; ++i) output[i] = raw.charCodeAt(i);
  return output;
}

export function isPushSupported() {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

/**
 * True when we are on iOS Safari and the app is NOT installed as PWA.
 * Push requires standalone display mode on iOS 16.4+.
 */
export function isIOSPWARequired() {
  const ua = navigator.userAgent || "";
  const isIOS = /iPad|iPhone|iPod/.test(ua);
  if (!isIOS) return false;
  const standalone = window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
  return !standalone;
}

async function getRegistration() {
  const reg = (await navigator.serviceWorker.getRegistration()) || (await navigator.serviceWorker.ready);
  return reg;
}

export async function getCurrentSubscription() {
  if (!isPushSupported()) return null;
  try {
    const reg = await getRegistration();
    return await reg.pushManager.getSubscription();
  } catch {
    return null;
  }
}

export async function subscribeToPush() {
  if (!isPushSupported()) throw new Error("Push non supportate su questo browser");
  if (!VAPID_PUBLIC_KEY) {
    // Fallback: fetch from backend if env is missing
    const { data } = await api.get("/push/vapid-public-key");
    if (!data?.public_key) throw new Error("VAPID public key non disponibile");
    return _subscribeWithKey(data.public_key);
  }
  return _subscribeWithKey(VAPID_PUBLIC_KEY);
}

async function _subscribeWithKey(publicKey) {
  const permission = await Notification.requestPermission();
  if (permission !== "granted") {
    throw new Error("Permesso notifiche non concesso");
  }
  const reg = await getRegistration();
  const sub = await reg.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(publicKey),
  });
  const json = sub.toJSON();
  await api.post("/push/subscribe", { endpoint: json.endpoint, keys: json.keys });
  return sub;
}

export async function unsubscribeFromPush() {
  const sub = await getCurrentSubscription();
  if (!sub) return { ok: true };
  const endpoint = sub.endpoint;
  await sub.unsubscribe().catch(() => {});
  try {
    await api.delete("/push/unsubscribe", { data: { endpoint } });
  } catch { /* noop */ }
  return { ok: true };
}
